import Button from '@rio-cloud/rio-uikit/Button';
import FormLabel from '@rio-cloud/rio-uikit/FormLabel';
import Select, { type SelectOption } from '@rio-cloud/rio-uikit/Select';
import { type ChangeEvent, type ReactElement, useEffect, useState } from 'react';
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl';
import { type DriverIdentificationFieldProps, IdentificationTypes } from '../../../../types';
import { ExternalDocumentationDialog } from '../../dialogs/components/ExternalDocumentationDialog';
import { existingDriverErrorString } from '../../dialogs/components/CreateDriverDialog';
import {
    countryMessageId,
    type DriverCardCountry,
    genericCountries,
    getCountryFromLocale,
    mapDriverCardCountryToPrefix,
    specificCountries,
} from '../../dialogs/driverCardCountry';
import { driversWithSimilarDriverCards, invalidDriverCardNumber } from '../../dialogs/driverCardValidation';
import { FormInputGroup } from '../../drivers/sidebar/commonElements/FormInputGroup';

const driverCardHelpLink = 'https://rio.cloud/en/glossar/driver-card?noCookie=1';

export const EuTachographIdentificationField = (props: DriverIdentificationFieldProps) => {
    const intl = useIntl();
    const [selectedCountry, setSelectedCountry] = useState<DriverCardCountry>(getCountryFromLocale(intl.locale));
    const [driverCardNumber, setDriverCardNumber] = useState<string | undefined>();
    const [showHelpDialog, setShowHelpDialog] = useState(false);

    useEffect(() => {
        setDriverCardNumber(undefined);
    }, [props.parentDialogShown]);

    const { onIdentificationChangeCallback, existingDrivers, currentDriver } = props;
    useEffect(() => {
        onIdentificationChangeCallback({
            value: generateIdentification(selectedCountry, driverCardNumber),
            isValid:
                !invalidDriverCardNumber(driverCardNumber) &&
                driversWithSimilarDriverCards(selectedCountry, driverCardNumber, existingDrivers, currentDriver)
                    .length === 0,
            type: IdentificationTypes.EU_TACHOGRAPH_CARD,
        });
    }, [selectedCountry, driverCardNumber, existingDrivers, currentDriver, onIdentificationChangeCallback]);

    const countryCodeOptions: SelectOption[] = sortByTranslation(intl, specificCountries)
        .map(
            (v: DriverCardCountry): SelectOption => ({
                id: v,
                label: <FormattedMessage id={countryMessageId(v)} />,
            })
        )
        .concat({
            id: 'OTHER_HEADER',
            label: <FormattedMessage id={'intl-msg:createDriver.addDriver.dialog.euDriverCard.genericCountryHeader'} />,
            header: true,
        })
        .concat(
            sortByTranslation(intl, genericCountries).map((v: DriverCardCountry) => ({
                id: v,
                label: <FormattedMessage id={countryMessageId(v)} />,
            }))
        );

    const handleSelectedCountryChange = (o: SelectOption | undefined) => {
        if (o) {
            setSelectedCountry(o.id as DriverCardCountry);
        }
    };
    const handleDriverCardNumberChange = (e: ChangeEvent<HTMLInputElement>) =>
        setDriverCardNumber(e.target.value.toUpperCase());

    const driverCardNumberErrorMessage = (): ReactElement | undefined => {
        if (driverCardNumber?.length === 16) {
            if (invalidDriverCardNumber(driverCardNumber)) {
                return <FormattedMessage id={'intl-msg:error.driverCard.invalid'} />;
            }
            const alreadyExistingDrivers = driversWithSimilarDriverCards(
                selectedCountry,
                driverCardNumber,
                props.existingDrivers,
                props.currentDriver
            );
            if (alreadyExistingDrivers.length > 0) {
                const existingDriverNamesAndStatus: string = alreadyExistingDrivers
                    .map(driver => {
                        return existingDriverErrorString(intl, driver);
                    })
                    .join(', ');
                return (
                    <span>
                        <FormattedMessage id={'intl-msg:error.driverCard.alreadyExists'} />
                        {': '}
                        {existingDriverNamesAndStatus}
                    </span>
                );
            }
        }
        return undefined;
    };

    const driverCardHelp = (
        <Button
            autoTrackingKey='drivers.documentation.driver-card.open'
            bsStyle={Button.PRIMARY}
            onClick={() => setShowHelpDialog(true)}
            variant={Button.VARIANT_LINK_INLINE}
        >
            <FormattedMessage id={'intl-msg:createDriver.addDriver.dialog.driverCardInfoLink.label'} />
        </Button>
    );

    return (
        <>
            <div className={props.className || 'col-12 col-sm-6 form-group'}>
                <FormLabel>
                    <span>
                        <FormattedMessage
                            id={'intl-msg:createDriver.addDriver.dialog.formLabel.euDriverCardIssuingCountry'}
                        />
                        *
                    </span>
                </FormLabel>
                <div data-testid='issuingCountryDropdown'>
                    <Select
                        value={[selectedCountry]}
                        onChange={handleSelectedCountryChange}
                        options={countryCodeOptions}
                        className='test-className'
                        btnClassName='test-btnClassName'
                        dropdownClassName='test-dropdownClassName'
                    />
                </div>
            </div>
            <div className={props.className || 'col-12 col-sm-6'}>
                <FormInputGroup
                    fieldError={driverCardNumberErrorMessage()}
                    fieldLabel={
                        <span>
                            <FormattedMessage
                                id={'intl-msg:createDriver.addDriver.dialog.formLabel.euDriverCardNumber'}
                            />
                            *
                        </span>
                    }
                    fieldLabelSupportingText={driverCardHelp}
                    fieldName={'driverCard'}
                    value={driverCardNumber || ''}
                    onChange={handleDriverCardNumberChange}
                    readOnly={false}
                    type={'text'}
                    requiredLength={16}
                    maxLength={16}
                    dataAttribute={'createDriverCardNumberField'}
                />
            </div>

            <ExternalDocumentationDialog
                iframeTitle={intl.formatMessage({
                    id: 'intl-msg:createDriver.addDriver.dialog.formLabel.euDriverCardNumber',
                })}
                onClose={() => setShowHelpDialog(false)}
                show={showHelpDialog}
                title={<FormattedMessage id='intl-msg:createDriver.addDriver.dialog.formLabel.euDriverCardNumber' />}
                trackingKey='drivers.documentation.driver-card'
                url={driverCardHelpLink}
            />
        </>
    );
};

const sortByTranslation = (intl: IntlShape, countries: DriverCardCountry[]): DriverCardCountry[] => {
    return countries
        .map(v => ({
            country: v,
            translation: intl.formatMessage({ id: countryMessageId(v) }),
        }))
        .sort((a, b) => a.translation.localeCompare(b.translation))
        .map(v => v.country);
};

const generateIdentification = (selectedCountry: DriverCardCountry, driverCardNumber: string | undefined) => {
    if (driverCardNumber) {
        return mapDriverCardCountryToPrefix(selectedCountry) + driverCardNumber;
    } else {
        return null;
    }
};
